(() => {
  if (window.__tanEditorialV36) return;
  window.__tanEditorialV36 = true;

  const sent = new Set();
  const observedRoots = new WeakSet();
  const allowedIntents = new Set(['all', 'inspiration', 'buying_guide', 'business', 'installation', 'project_story', 'general', 'related']);
  const allowedRoutes = new Set(['topic_filter', 'article_card', 'article_title', 'article_action', 'primary_cta', 'related_project', 'related_collection', 'back_to_blog']);
  const designMode = () => document.documentElement.classList.contains('shopify-design-mode');
  const cleanIntent = (value) => allowedIntents.has(String(value || '')) ? String(value) : 'other';
  const cleanRoute = (value) => allowedRoutes.has(String(value || '')) ? String(value) : 'other';

  const publish = (name, data = {}, onceKey = '') => {
    if (designMode()) return;
    if (onceKey && sent.has(onceKey)) return;
    if (onceKey) sent.add(onceKey);
    const payload = {
      event_version: '1.0',
      language_code: String(document.documentElement.lang || '').slice(0, 20),
      ...data,
    };
    window.dispatchEvent(new CustomEvent('tan:editorial-event', { detail: { name, data: payload } }));
    try {
      window.Shopify?.analytics?.publish?.(`the_art_neon:${name}`, payload);
    } catch (_) {
      // Reading and navigation remain available when analytics is unavailable.
    }
  };

  const buildToc = (root) => {
    if (!(root instanceof Element) || root.dataset.tanTocReady === 'true') return;
    root.dataset.tanTocReady = 'true';
    const articleBody = root.querySelector('[data-tan-article-body]');
    const lists = Array.from(root.querySelectorAll('[data-tan-toc-list]'));
    if (!articleBody || lists.length === 0) return;

    const headings = Array.from(articleBody.querySelectorAll('h2, h3')).filter((heading) => heading.textContent?.trim());
    if (headings.length === 0) return;

    const reserved = new Set(Array.from(document.querySelectorAll('[id]')).map((element) => element.id).filter(Boolean));
    const headingIds = new Set();

    headings.forEach((heading, index) => {
      let id = heading.id || `article-section-${index + 1}`;
      if (!heading.id || headingIds.has(id)) {
        let suffix = index + 1;
        id = `article-section-${suffix}`;
        while (reserved.has(id) || headingIds.has(id)) {
          suffix += 1;
          id = `article-section-${suffix}`;
        }
        heading.id = id;
      }
      headingIds.add(id);
      reserved.add(id);
      if (!heading.hasAttribute('tabindex')) heading.tabIndex = -1;

      lists.forEach((list) => {
        const item = document.createElement('li');
        item.dataset.level = heading.tagName === 'H3' ? '3' : '2';
        const link = document.createElement('a');
        link.href = `#${encodeURIComponent(id)}`;
        link.textContent = heading.textContent.trim();
        link.addEventListener('click', () => {
          window.setTimeout(() => heading.focus({ preventScroll: true }), 0);
        });
        item.append(link);
        list.append(item);
      });
    });

    root.querySelectorAll('[data-tan-toc-shell], [data-tan-toc-mobile]').forEach((element) => {
      element.hidden = false;
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (!visible) return;
        const activeHash = `#${encodeURIComponent(visible.target.id)}`;
        lists.forEach((list) => {
          list.querySelectorAll('a').forEach((link) => {
            if (link.getAttribute('href') === activeHash) link.setAttribute('aria-current', 'location');
            else link.removeAttribute('aria-current');
          });
        });
      }, { rootMargin: '-18% 0px -70% 0px', threshold: 0 });
      headings.forEach((heading) => observer.observe(heading));
    }
  };

  const copyCurrentLink = async () => {
    const cleanUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(cleanUrl);
      return true;
    }
    const input = document.createElement('textarea');
    input.value = cleanUrl;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.append(input);
    input.select();
    const copied = document.execCommand('copy');
    input.remove();
    return copied;
  };

  const bindShare = (root) => {
    root.querySelectorAll('[data-tan-share]:not([data-tan-share-bound])').forEach((button) => {
      button.dataset.tanShareBound = 'true';
      button.hidden = false;
      button.addEventListener('click', async () => {
        const status = button.parentElement?.querySelector('[data-tan-share-status]');
        try {
          if (navigator.share) {
            await navigator.share({ title: document.title, url: window.location.href.split('#')[0] });
            publish('editorial_share_used', { share_method: 'native' });
            return;
          }
          const copied = await copyCurrentLink();
          if (copied && status) status.textContent = status.dataset.copiedLabel || '';
          if (copied) publish('editorial_share_used', { share_method: 'copy' });
        } catch (_) {
          // A cancelled share should not interrupt the article.
        }
      });
    });
  };

  const bindRoutes = (root) => {
    root.querySelectorAll('[data-tan-editorial-route]:not([data-tan-editorial-bound])').forEach((link) => {
      link.dataset.tanEditorialBound = 'true';
      link.addEventListener('click', () => {
        publish('editorial_route_opened', {
          route_type: cleanRoute(link.dataset.routeType),
          intent_key: cleanIntent(link.dataset.intentKey),
        });
      });
    });

    root.querySelectorAll('[data-tan-blog-search]:not([data-tan-search-bound])').forEach((form) => {
      form.dataset.tanSearchBound = 'true';
      form.addEventListener('submit', () => publish('editorial_search_started', { content_type: 'article' }));
    });
  };

  const observePrimaryRoute = (root) => {
    if (observedRoots.has(root) || designMode()) return;
    observedRoots.add(root);
    const cta = root.querySelector('[data-tan-primary-cta]');
    if (!cta || !('IntersectionObserver' in window)) return;
    const link = cta.querySelector('[data-intent-key]');
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      publish('editorial_primary_route_viewed', { intent_key: cleanIntent(link?.dataset.intentKey) }, 'editorial-primary-route-view');
      observer.disconnect();
    }, { threshold: 0.3 });
    observer.observe(cta);
  };

  const initialize = (scope = document) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-tan-editorial-root]')) roots.push(scope);
    scope.querySelectorAll?.('[data-tan-editorial-root]').forEach((root) => roots.push(root));
    roots.forEach((root) => {
      buildToc(root);
      bindShare(root);
      bindRoutes(root);
      observePrimaryRoute(root);
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
